import type { MantineSize } from "@mantine/core";

import {
  DateInput as MantineDateInput,
  DatePickerInput as MantineDatePickerInput,
  DateTimePicker as MantineDateTimePicker,
  TimeInput as MantineTimeInput,
} from "@mantine/dates";
import * as React from "react";
import type { ElementProps, SizeValue, StyleProps } from "../primitives/style-props";

/**
 * Date composites: DatePickerInput and DateInput (SC-2699).
 *
 * THE VALUE CONTRACT — this is the whole reason the module exists, and it is unchanged by these
 * wrappers.
 *
 * Both controls emit a browser-local Date, unconverted. They differ in one respect, and the
 * difference is not cosmetic — each is stated separately because conflating them is exactly the
 * kind of wrong note that would licence a bad change later:
 *
 * - DateInput PRESERVES the time-of-day. preserveTime defaults to true, so a calendar click emits
 *   assignTime(previousValue, clickedDay): the clicked day at browser-local midnight when the
 *   previous value was null, and that day carrying the previous value's local clock otherwise.
 * - DatePickerInput does NOT. It has no preserveTime and no assignTime; a click sets the clicked
 *   day as constructed by the calendar, at browser-local midnight, discarding any time-of-day the
 *   previous value carried. Its emitted shape follows `type`: Date | null, Date[], or
 *   [Date | null, Date | null].
 *
 * Clearing follows the same per-arm shape, and is NOT uniformly null: DateInput emits null, and so
 * does DatePickerInput's default arm, but its range arm emits [null, null] and its multiple arm
 * emits [] (use-dates-input.js onClear). A call site testing `value === null` on a range picker
 * never fires.
 *
 * `ref` is deliberately not on the DatePickerInput surface: Mantine forwards one to the trigger
 * button, but no call site takes it, so the wrapper stays a plain generic function rather than
 * carrying the forwardRef-plus-cast machinery Select needs. DateInput does forward its ref.
 *
 * Some vendor levers are deliberately NOT restated on the owned surface, so they cannot be set
 * through these wrappers at all: `preserveTime` and `fixOnBlur` on DateInput, and `allowDeselect`,
 * `allowSingleDateInRange`, `sortDates`, `closeOnChange` and `dropdownType` on DatePickerInput. No
 * call site passes any of them; the vendor default stands. Add one back only with the call site
 * that needs it, and re-read the emit contract above first — three of them change what is emitted.
 *
 * These wrappers add NO normalisation and declare NO default the vendor does not already apply,
 * with two exceptions that restore Mantine 6 defaults rather than invent them: Mantine 7 flipped
 * Popover's `withinPortal` default to true, so the wrappers put the Mantine 6 inline dropdown back
 * (withMantine6PortalDefault below), and Mantine 7's PickerInputBase stopped returning focus on
 * close, so DatePickerInput and DateTimePicker put Mantine 6's `returnFocus` back
 * (withMantine6PickerFocusReturn below, SC-2703). They forward `onChange` unwrapped and set no owned
 * `valueFormat`, `dateParser`, `allowDeselect` or `preserveTime`, and no other `popoverProps` key.
 * Converting to an organisation-timezone boundary is the call site's job, through
 * pickerDateToEndOfDayUTC / pickerDateToStartOfDayUTC in ~/utils/datetime —
 * see docs/TIMEZONE_STANDARDS.md, which states that the org timezone always drives date
 * calculations (never the browser's and never raw UTC), that a picker value is stored as-is
 * (Pattern 1) or normalised in the submit handler (Pattern 2), and that normalising twice shifts
 * the day (Pitfall 3). Over 25 call sites already convert downstream, so a conversion here would
 * double-normalise every one of them.
 *
 * This is the same decision controls/README.md records for NumberInput, the highest-risk control
 * in the tier below: the parsing lives in plain functions supplied by the call sites and the
 * wrapper adds none of its own.
 *
 * The risk is silent. A wrapper that normalised to start-of-day, or defaulted preserveTime to
 * false, would still typecheck and still render — and would shift the emitted instant on pages
 * like the income statement, which holds org-noon instants as picker values. `dates-dom.test.tsx`
 * proves the emitted value against the vendor component itself rather than asserting it here.
 *
 * Mantine 7 (SC-2703) keeps this contract unchanged: 7.x values are still Dates, DateInput still
 * defaults preserveTime to true and emits assignTime(previousValue, clickedDay), and the picker's
 * emit, clear and range paths (use-dates-state, use-dates-input) are line-for-line the Mantine 6
 * ones. With no DatesProvider timezone, 7.x's new timezone shift is the identity. What 7.x did change
 * is absorbed below without touching the owned props: `icon` became `leftSection`, TimeInput's
 * `iconWidth` became `leftSectionWidth`, the calendar-typed `size` narrowed, the portal default
 * flipped, and the pickers' popover stopped returning focus on close.
 */

/** A single date, or null when cleared. */
export type DateValue = Date | null;
export type DatesRangeValue = [DateValue, DateValue];
export type DatePickerInputType = "default" | "multiple" | "range";

/**
 * Restated exactly as Mantine types it: `type` flips the value contract between one date, a list
 * of dates, and a start/end pair. 30 call sites use the range arm and one names the default arm
 * explicitly, so collapsing this to the scalar would silently retype every range picker.
 */
export type DatePickerInputValue<Type extends DatePickerInputType = "default"> = Type extends "range"
  ? DatesRangeValue
  : Type extends "multiple"
    ? Date[]
    : DateValue;

/**
 * The label and feedback surface shared by both date controls. Matches what form.getInputProps()
 * feeds (`value`, `onChange`, `error`) so the @mantine/form carve-out recorded in
 * controls/README.md holds with no change to any form.
 */
interface DateFieldProps {
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  withAsterisk?: boolean;
  required?: boolean;
  placeholder?: string;
  /**
   * Owned name at the Mantine 6 spelling. Mantine 7 renamed it `leftSection`; each wrapper forwards
   * it under that name (SC-2703), so the owned name is unchanged on every control.
   */
  icon?: React.ReactNode;
  /**
   * Adds a clear button. It does NOT also enable deselect-by-reclick on both controls: DateInput
   * derives `_allowDeselect` from `clearable` (DateInput.js), while DatePickerInput gates deselect
   * on `allowDeselect` alone (use-dates-state.js) and never reads `clearable` for it.
   */
  clearable?: boolean;
  /** Dayjs format for the displayed text. Mantine's own default is "MMMM D, YYYY". */
  valueFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  /**
   * Narrowed to the one key the call sites pass. The dropdown renders inline unless this portals
   * it, which is the preference an ancestor with `overflow: hidden` clips — the same silent
   * regression the overlay wrappers pin. Mantine 7 portals by default; the wrappers keep Mantine 6's
   * inline default (SC-2703). The bare rest of Mantine's Popover surface is deliberately not
   * restated, the way SegmentedControl restates only the object form of `data`.
   */
  popoverProps?: { withinPortal?: boolean; zIndex?: number };
  /** Non-numeric: Mantine resolves input size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
}

/**
 * Mantine 6's Popover rendered inline unless told to portal; Mantine 7's portals by default. The
 * owned `popoverProps` contract promises the Mantine 6 default, so an omitted or undefined
 * `withinPortal` renders inline and an explicit value still wins (SC-2703). This is the one portal
 * default the module sets: dates-boundary pins this helper's exact text and every place it is applied.
 */
const withMantine6PortalDefault = (popoverProps: DateFieldProps["popoverProps"]) => ({
  ...popoverProps,
  withinPortal: popoverProps?.withinPortal ?? false,
});

/**
 * Mantine 6's PickerInputBase handed its Popover `returnFocus: true`, so closing the dropdown put focus
 * back on the trigger; Mantine 7's hands it `false` (`PickerInputBase.mjs`). DatePickerInput and
 * DateTimePicker render through PickerInputBase and restore it here; DateInput renders its own Popover,
 * which returned no focus in either version, so it keeps withMantine6PortalDefault alone. The caller's
 * `popoverProps` still spread last and win (SC-2703). dates-boundary pins this helper's exact text and
 * both places it is applied.
 */
const withMantine6PickerFocusReturn = (popoverProps: DateFieldProps["popoverProps"]) => ({
  returnFocus: true,
  ...withMantine6PortalDefault(popoverProps),
});

/**
 * Mantine 7 types the date controls' `size` as its five theme keys alone (it reaches them through the
 * calendar's month settings), where Mantine 6 accepted any string. The owned value is forwarded
 * unchanged, as it always was: the field sizes itself through Input, whose own `size` still takes any
 * string. AGENTS.md D01b: dates-casts pins Mantine's declaration and that Input accepts every owned
 * size, so a change to either side fails the typecheck (SC-2703).
 */
const toVendorSize = (size: DateFieldProps["size"]) => size as MantineSize | undefined;

/** Mantine 7 renamed the inputs' `icon` and `iconWidth` to `leftSection` and `leftSectionWidth` (SC-2703). */
type WithVendorSections<Props extends TimeInputProps> = Omit<Props, "icon" | "iconWidth"> & {
  leftSection?: React.ReactNode;
  leftSectionWidth?: TimeInputProps["iconWidth"];
};

/** Forwards the owned section names under Mantine 7's; the owned names are unchanged. */
const toVendorSections = <Props extends TimeInputProps>({ icon, iconWidth, ...props }: Props): WithVendorSections<Props> => ({
  ...props,
  leftSection: icon,
  leftSectionWidth: iconWidth,
});

export interface DateInputProps
  extends Omit<ElementProps<"input">, "size" | "value" | "defaultValue" | "onChange">, StyleProps, DateFieldProps {
  value?: DateValue;
  defaultValue?: DateValue;
  onChange?(value: DateValue): void;
  /** Parses typed text into a date. Supplied by the call site; this wrapper sets none. */
  dateParser?: (value: string) => Date | null;
  /** Allows clearing by re-clicking the selected day. Mantine ties its own default to `clearable`. */
  allowDeselect?: boolean;
}

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { icon, popoverProps, size, ...props },
  ref,
) {
  return (
    <MantineDateInput
      {...props}
      leftSection={icon}
      popoverProps={withMantine6PortalDefault(popoverProps)}
      size={toVendorSize(size)}
      data-sc-input="true"
      ref={ref}
    />
  );
});

export interface DatePickerInputProps<Type extends DatePickerInputType = "default">
  extends Omit<ElementProps<"button">, "value" | "defaultValue" | "onChange" | "type">, StyleProps, DateFieldProps {
  /**
   * Restated widened, exactly as Mantine declares it (`type?: DatePickerType | Type`). Narrowing
   * this to `type?: Type` would change inference at every call site, which is a divergence from
   * the vendor rather than a port of it.
   */
  type?: DatePickerInputType | Type;
  value?: DatePickerInputValue<Type>;
  defaultValue?: DatePickerInputValue<Type>;
  onChange?(value: DatePickerInputValue<Type>): void;
  /**
   * A class for the label element. `className` reaches only the root, so the one call site that
   * restyles the label needs this narrow owned prop rather than the vendor's `styles` map. Same
   * owned-name-forwarding-one-class shape as Alert's `wrapperClassName`.
   */
  labelClassName?: string;
}

export function DatePickerInput<Type extends DatePickerInputType = "default">({
  labelClassName,
  icon,
  popoverProps,
  size,
  ...props
}: DatePickerInputProps<Type>): React.JSX.Element {
  return (
    <MantineDatePickerInput<Type>
      {...props}
      classNames={labelClassName ? { label: labelClassName } : undefined}
      leftSection={icon}
      popoverProps={withMantine6PickerFocusReturn(popoverProps)}
      size={toVendorSize(size)}
      data-sc-input="true"
    />
  );
}

/** Date-and-time control value: a browser-local Date, or null when cleared. */
export type DateTimePickerValue = DateValue;

export interface DateTimePickerProps
  extends Omit<ElementProps<"button">, "defaultValue" | "onChange" | "type" | "value">, StyleProps, DateFieldProps {
  value?: DateTimePickerValue;
  defaultValue?: DateTimePickerValue;
  onChange?(value: DateTimePickerValue): void;
  /** Props forwarded to the time field inside the calendar popover. */
  timeInputProps?: TimeInputProps & { ref?: React.Ref<HTMLInputElement> };
  /** Props forwarded to the calendar's confirm button. */
  submitButtonProps?: ElementProps<"button"> & StyleProps;
  withSeconds?: boolean;
}

/**
 * The vendor accepts a broad DateInputSharedProps intersection. This private adapter limits the
 * public surface to the behavior current call sites use; dates-casts pins the forwarded contract.
 * It takes the section props under their Mantine 7 names, and the popover props with the restored
 * `returnFocus`, which is all the wrapper forwards.
 */
const DateTimePickerRoot = MantineDateTimePicker as React.ComponentType<
  Omit<DateTimePickerProps, "icon" | "popoverProps" | "timeInputProps"> & {
    leftSection?: React.ReactNode;
    popoverProps?: ReturnType<typeof withMantine6PickerFocusReturn>;
    timeInputProps?: WithVendorSections<NonNullable<DateTimePickerProps["timeInputProps"]>>;
    readonly ref?: React.Ref<HTMLButtonElement>;
  }
>;

export const DateTimePicker = React.forwardRef<HTMLButtonElement, DateTimePickerProps>(function DateTimePicker(
  { icon, popoverProps, timeInputProps = {}, ...props },
  ref,
) {
  const ownedTimeInputProps = { ...toVendorSections(timeInputProps), "data-sc-input": "true" };

  return (
    <DateTimePickerRoot
      {...props}
      leftSection={icon}
      popoverProps={withMantine6PickerFocusReturn(popoverProps)}
      data-sc-input="true"
      ref={ref}
      timeInputProps={ownedTimeInputProps}
    />
  );
});

/** Native time field value remains the browser's HH:mm string; adapters live at their caller. */
export interface TimeInputProps extends Omit<ElementProps<"input">, "size">, StyleProps {
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  withAsterisk?: boolean;
  icon?: React.ReactNode;
  iconWidth?: React.CSSProperties["width"];
  rightSection?: React.ReactNode;
  rightSectionWidth?: React.CSSProperties["width"];
  rightSectionProps?: Record<string, unknown>;
  variant?: "default" | "filled" | "unstyled";
  size?: Exclude<SizeValue, number>;
  withSeconds?: boolean;
}

export const TimeInput = React.forwardRef<HTMLInputElement, TimeInputProps>(function TimeInput(props, ref) {
  return <MantineTimeInput {...toVendorSections(props)} data-sc-input="true" ref={ref} />;
});
